import { GAME_SPEEDS, type GameSpeed } from '../config/gameConfig';
import { economyConfig } from '../config/economyConfig';
import { getBuilding } from '../data/buildings';
import { getDistrict } from '../data/districts';
import { getStaffType } from '../data/staff';
import { getUpgrade } from '../data/upgrades';
import { createStaffMember } from '../models/Staff';
import { DemandEngine } from './DemandEngine';
import { EventEngine } from './EventEngine';
import { MarketEngine } from './MarketEngine';
import { StaffEngine } from './StaffEngine';
import { UpgradeEngine } from './UpgradeEngine';
import {
  createNewGameState,
  type GameState,
  type NewGameOptions,
} from '../models/GameState';
import { createParkingLot, type ParkingLot } from '../models/ParkingLot';
import { GameSimulation, type SimTickResult } from './GameSimulation';
import { ParkingLotEngine } from './ParkingLotEngine';
import { ValuationEngine } from './ValuationEngine';
import { VehicleEngine } from './VehicleEngine';
import { SaveManager } from './SaveManager';

export type BuildLotResult =
  | { ok: true; lotId: string }
  | { ok: false; reason: 'unknown-district' | 'already-exists' | 'insufficient-funds' };

export type TileActionResult =
  | { ok: true }
  | { ok: false; reason: string };

export type StoreListener = (state: GameState) => void;

/**
 * GameStore: the single source of truth for the running game (spec: Custom
 * GameStore, section 2).
 *
 * It owns the GameState, drives the simulation loop, exposes intent-style
 * actions (newGame / setSpeed / pause / …), and lets UI subscribe to changes.
 * It contains NO Phaser code, so scenes render *from* it but the game does not
 * depend on scenes. This is the seam a multiplayer client/server would plug
 * into later.
 */
export class GameStore {
  private state: GameState;
  private readonly simulation = new GameSimulation();
  private readonly listeners = new Set<StoreListener>();

  /** Auto-save cadence: once per in-game day (spec section 25). */
  private autoSaveEnabled = true;

  constructor(initial?: GameState) {
    this.state = initial ?? createNewGameState();
  }

  // --- state access ---

  getState(): Readonly<GameState> {
    return this.state;
  }

  // --- subscription ---

  subscribe(listener: StoreListener): () => void {
    this.listeners.add(listener);
    // Emit current state immediately so subscribers can initialize.
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.state);
  }

  // --- lifecycle actions ---

  newGame(options: NewGameOptions = {}): void {
    this.state = createNewGameState(options);
    this.simulation.reset();
    this.emit();
  }

  loadGame(): boolean {
    const loaded = SaveManager.load();
    if (!loaded) return false;
    this.state = loaded;
    this.simulation.reset();
    this.emit();
    return true;
  }

  /** Load an arbitrary state object (e.g. a cloud save). Minimal validation. */
  loadFromData(data: unknown): boolean {
    const d = data as Partial<GameState> | null;
    if (!d || typeof d !== 'object' || !d.player || !Array.isArray(d.parkingLots)) {
      return false;
    }
    this.state = d as GameState;
    this.simulation.reset();
    this.emit();
    return true;
  }

  saveGame(): boolean {
    return SaveManager.save(this.state);
  }

  hasSave(): boolean {
    return SaveManager.hasSave();
  }

  // --- clock / speed control (spec section 4) ---

  setSpeed(speed: GameSpeed): void {
    if (!GAME_SPEEDS.includes(speed)) return;
    this.state.speed = speed;
    this.state.paused = speed === 0;
    this.simulation.reset();
    this.emit();
  }

  getSpeed(): GameSpeed {
    return this.state.speed;
  }

  pause(): void {
    this.state.paused = true;
    this.emit();
  }

  resume(): void {
    this.state.paused = false;
    if (this.state.speed === 0) this.state.speed = 1;
    this.simulation.reset();
    this.emit();
  }

  togglePause(): void {
    if (this.state.paused) this.resume();
    else this.pause();
  }

  isPaused(): boolean {
    return this.state.paused;
  }

  // --- parking lots / construction (spec sections 7, 8, 28) ---

  getLots(): ReadonlyArray<ParkingLot> {
    return this.state.parkingLots;
  }

  getLot(lotId: string): ParkingLot | undefined {
    return this.state.parkingLots.find((l) => l.id === lotId);
  }

  getLotByDistrict(districtId: string): ParkingLot | undefined {
    return this.state.parkingLots.find((l) => l.districtId === districtId);
  }

  /**
   * Open a new parking lot in a district. Deducts the district land price and
   * creates an empty grid. One lot per district in the MVP.
   */
  buildLot(districtId: string): BuildLotResult {
    const district = getDistrict(districtId);
    if (!district) return { ok: false, reason: 'unknown-district' };
    if (this.getLotByDistrict(districtId)) {
      return { ok: false, reason: 'already-exists' };
    }
    if (this.state.player.cash < district.landPrice) {
      return { ok: false, reason: 'insufficient-funds' };
    }

    const lotId = `lot-${districtId}`;
    const lot = createParkingLot({
      id: lotId,
      name: `${district.name} Lot`,
      districtId,
    });

    // Land is capital expenditure: it reduces cash but is not an operating
    // expense in the P&L (it becomes company asset value later, spec 38).
    this.state.player.cash -= district.landPrice;
    this.state.parkingLots.push(lot);
    this.state.player.parkingLotIds.push(lotId);
    this.emit();

    return { ok: true, lotId };
  }

  /** Place a building tile, deducting its cost. Fails on overlap / no funds. */
  placeTile(
    lotId: string,
    x: number,
    y: number,
    buildingId: string
  ): TileActionResult {
    const lot = this.getLot(lotId);
    if (!lot) return { ok: false, reason: 'unknown-lot' };
    const def = getBuilding(buildingId);
    if (!def) return { ok: false, reason: 'unknown-building' };

    const check = ParkingLotEngine.canPlace(
      lot,
      x,
      y,
      def,
      this.state.player.cash
    );
    if (!check.ok) return check;

    const cost = ParkingLotEngine.place(lot, x, y, def);
    // Construction is capital expenditure (reduces cash, not P&L opex).
    this.state.player.cash -= cost;
    // Tile changes (CCTV, cashier booth, capacity) affect staff-derived stats.
    StaffEngine.applyEffects(lot);
    this.emit();
    return { ok: true };
  }

  // --- pricing (spec sections 12, 31) ---

  /** Set a lot's hourly parking price (clamped to configured bounds). */
  setLotPrice(lotId: string, price: number): void {
    const lot = this.getLot(lotId);
    if (!lot) return;
    const clamped = Math.round(
      Math.min(economyConfig.PRICE_MAX, Math.max(economyConfig.PRICE_MIN, price))
    );
    if (clamped === lot.parkingPrice) return;
    lot.parkingPrice = clamped;
    this.emit();
  }

  /** Adjust a lot's price by one configured step (+/-). */
  adjustLotPrice(lotId: string, direction: 1 | -1): void {
    const lot = this.getLot(lotId);
    if (!lot) return;
    this.setLotPrice(
      lotId,
      lot.parkingPrice + direction * economyConfig.PRICE_STEP
    );
  }

  /** Current estimated demand (cars/hour) for a lot, for UI readouts. */
  getDemandEstimate(lotId: string): number {
    const lot = this.getLot(lotId);
    if (!lot) return 0;
    return DemandEngine.computeDemandScore(lot, this.state.time);
  }

  // --- staff (spec sections 14, 32) ---

  /** Hire one staff member of a type onto a lot (salary is recurring). */
  hireStaff(lotId: string, typeId: string): TileActionResult {
    const lot = this.getLot(lotId);
    if (!lot) return { ok: false, reason: 'unknown-lot' };
    const def = getStaffType(typeId);
    if (!def) return { ok: false, reason: 'unknown-staff-type' };

    const member = createStaffMember({
      id: `${lot.id}-s${lot.staffSeq++}`,
      typeId: def.id,
      salary: def.salary,
    });
    lot.staff.push(member);
    StaffEngine.applyEffects(lot);
    this.emit();
    return { ok: true };
  }

  /** Fire the most recently hired staff member of a type (if any). */
  fireStaff(lotId: string, typeId: string): TileActionResult {
    const lot = this.getLot(lotId);
    if (!lot) return { ok: false, reason: 'unknown-lot' };
    const idx = lot.staff.map((s) => s.typeId).lastIndexOf(typeId);
    if (idx < 0) return { ok: false, reason: 'none-of-type' };
    lot.staff.splice(idx, 1);
    StaffEngine.applyEffects(lot);
    this.emit();
    return { ok: true };
  }

  getStaffCount(lotId: string, typeId: string): number {
    const lot = this.getLot(lotId);
    if (!lot) return 0;
    return lot.staff.reduce((n, s) => (s.typeId === typeId ? n + 1 : n), 0);
  }

  /** Whether a lot can collect parking fees (cashier / machine / automation). */
  canCollectPayment(lotId: string): boolean {
    const lot = this.getLot(lotId);
    return lot ? VehicleEngine.canCollectPayment(lot) : false;
  }

  /** Whether a lot can admit cars (auto barrier / LPR, or a cashier at the gate). */
  canAdmitEntry(lotId: string): boolean {
    const lot = this.getLot(lotId);
    return lot ? VehicleEngine.canAdmitEntry(lot) : false;
  }

  // --- upgrades (spec sections 17, 34) ---

  /** Purchase an upgrade for a lot (one-time capex + recurring maintenance). */
  purchaseUpgrade(lotId: string, upgradeId: string): TileActionResult {
    const lot = this.getLot(lotId);
    if (!lot) return { ok: false, reason: 'unknown-lot' };
    const def = getUpgrade(upgradeId);
    if (!def) return { ok: false, reason: 'unknown-upgrade' };

    const check = UpgradeEngine.canPurchase(lot, def, this.state.player.cash);
    if (!check.ok) return { ok: false, reason: check.reason };

    // One-time cost is capital expenditure (cash only, not P&L opex).
    this.state.player.cash -= def.cost;
    lot.upgrades.push(def.id);
    // Some upgrades (CCTV) change security, which is a staff-derived stat.
    StaffEngine.applyEffects(lot);
    this.emit();
    return { ok: true };
  }

  ownsUpgrade(lotId: string, upgradeId: string): boolean {
    const lot = this.getLot(lotId);
    return lot ? lot.upgrades.includes(upgradeId) : false;
  }

  // --- competitors / market (spec sections 20, 21) ---

  getCompetitors(): ReadonlyArray<import('../models/Competitor').Competitor> {
    return this.state.competitors;
  }

  /** Overall city market share per company (player included), sorted desc. */
  getMarketShares(): ReturnType<typeof MarketEngine.overallShares> {
    return MarketEngine.overallShares(this.state, this.state.time);
  }

  /** The player's overall market share (0..1). */
  getPlayerMarketShare(): number {
    const shares = this.getMarketShares();
    return shares.find((s) => s.ownerId === 'player')?.share ?? 0;
  }

  // --- events / notifications (spec sections 22, 24, 36) ---

  getNotifications(): ReadonlyArray<GameState['notifications'][number]> {
    return this.state.notifications;
  }

  getActiveEvents(): ReadonlyArray<GameState['events'][number]> {
    return EventEngine.active(this.state, this.state.time.totalMinutes);
  }

  // --- valuation / objective (spec section 38) ---

  getValuation(): ReturnType<typeof ValuationEngine.compute> {
    return ValuationEngine.compute(this.state);
  }

  isObjectiveAchieved(): boolean {
    return this.state.objectiveAchieved;
  }

  // --- expansion (spec sections 19, 37) ---

  /** Info for the expand button: next size, cost, and whether it's allowed. */
  getExpansionInfo(lotId: string): {
    nextWidth: number;
    nextHeight: number;
    cost: number;
    atMax: boolean;
  } | null {
    const lot = this.getLot(lotId);
    if (!lot) return null;
    const step = economyConfig.EXPANSION_STEP;
    return {
      nextWidth: lot.width + step,
      nextHeight: lot.height + step,
      cost: economyConfig.EXPANSION_COST,
      atMax: lot.width + step > economyConfig.MAX_LOT_DIM,
    };
  }

  /** Grow a lot's grid by one expansion step (capital expenditure). */
  expandLot(lotId: string): TileActionResult {
    const lot = this.getLot(lotId);
    if (!lot) return { ok: false, reason: 'unknown-lot' };
    const step = economyConfig.EXPANSION_STEP;
    const newW = lot.width + step;
    const newH = lot.height + step;
    if (newW > economyConfig.MAX_LOT_DIM) return { ok: false, reason: 'max-size' };
    if (this.state.player.cash < economyConfig.EXPANSION_COST) {
      return { ok: false, reason: 'insufficient-funds' };
    }

    this.state.player.cash -= economyConfig.EXPANSION_COST; // capex
    ParkingLotEngine.expand(lot, newW, newH);
    // Construction clears the lot of in-flight cars (tile indices shift).
    lot.vehicles = [];
    lot.occupiedSpaces = 0;
    StaffEngine.applyEffects(lot);
    this.emit();
    return { ok: true };
  }

  /** Clear a tile back to empty (no refund in the MVP). */
  removeTile(lotId: string, x: number, y: number): TileActionResult {
    const lot = this.getLot(lotId);
    if (!lot) return { ok: false, reason: 'unknown-lot' };
    const removed = ParkingLotEngine.remove(lot, x, y);
    if (!removed) return { ok: false, reason: 'empty' };
    StaffEngine.applyEffects(lot);
    this.emit();
    return { ok: true };
  }

  // --- finance (spec sections 13, 23) ---

  getFinance(): Readonly<GameState['finance']> {
    return this.state.finance;
  }

  /** The most recently settled day's P&L, or null if none yet. */
  getLastDailyRecord(): GameState['finance']['history'][number] | null {
    const h = this.state.finance.history;
    return h.length > 0 ? h[h.length - 1] : null;
  }

  // --- main loop tick, called by the render loop with real elapsed ms ---

  tick(realDeltaMs: number): SimTickResult {
    const result = this.simulation.update(this.state, realDeltaMs);

    if (result.dayChanged && this.autoSaveEnabled) {
      this.saveGame();
    }

    // Only re-render/broadcast when time actually advanced, to avoid spamming
    // subscribers every frame while paused.
    if (result.ticks > 0) this.emit();

    return result;
  }

  setAutoSave(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
  }
}

/**
 * A single shared store instance for the app. Scenes import this so they all
 * read/write the same game. (Tests construct their own GameStore instead.)
 */
export const gameStore = new GameStore();
